import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, Eye, Play, RefreshCw, Trash2, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const POLL_INTERVAL_MS = 2000;
const HEARTBEAT_STALE_SECONDS = 60;

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

function secondsSince(isoString) {
  if (!isoString) return null;
  const then = new Date(isoString).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((Date.now() - then) / 1000));
}

function fmtTimestamp(isoString) {
  if (!isoString) return 'never';
  const then = new Date(isoString);
  if (Number.isNaN(then.getTime())) return 'never';
  return then.toLocaleString('en-GB');
}

// Mirrors InvoiceTaxonomyBackfillPanel.jsx's useJobPolling, parameterised by
// which job id to poll -- independent poll/elapsed refs per operation so
// recovering/polling Preview can never stop Apply's poll, or vice versa.
// pollFn defaults to the Non-Invoice Cleanup job-status call; the
// Type-classify-suspects sub-section below passes its own.
function useJobPolling({ onCompleted, onFailed, onProgress, pollFn }) {
  const [polling, setPolling] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const pollRef = useRef(null);
  const elapsedRef = useRef(null);
  const startTimeRef = useRef(null);

  const stop = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (elapsedRef.current) { clearInterval(elapsedRef.current); elapsedRef.current = null; }
    startTimeRef.current = null;
    setPolling(false);
  }, []);

  useEffect(() => stop, [stop]);

  const start = useCallback((jobId) => {
    stop();
    setPolling(true);
    setElapsed(0);
    startTimeRef.current = Date.now();

    elapsedRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);

    pollRef.current = setInterval(async () => {
      try {
        const { data } = await (pollFn ? pollFn(jobId) : diagnosticsAPI.nonInvoiceCleanupJobStatus(jobId));
        if (data.status === 'completed') {
          stop();
          onCompleted(data.result);
        } else if (data.status === 'failed') {
          stop();
          onFailed(data.error, data.progress || null);
        } else if (data.status === 'running') {
          onProgress(data.progress || null);
        }
      } catch (err) {
        stop();
        toast.error(getErrorMessage(err));
      }
    }, POLL_INTERVAL_MS);
  }, [stop, onCompleted, onFailed, onProgress, pollFn]);

  return { polling, elapsed, start };
}

function ChainSection({
  operation, title, icon, chain, progress, failed, polling, elapsed, starting, stopping,
  onRun, onStop, runLabel, runDisabled,
}) {
  const running = chain?.status === 'running';
  const live = progress || chain?.seed;
  const cohortCount = chain?.metadata?.cohort_count;
  const processed = live?.cohort_processed;
  const heartbeatAge = secondsSince(chain?.heartbeat_at);
  const stalled = running && !polling && heartbeatAge != null && heartbeatAge > HEARTBEAT_STALE_SECONDS;
  const byReason = live?.by_reason || {};
  const samples = live?.samples || {};
  const errorText = (chain?.status === 'error' && chain?.error) || failed;

  return (
    <div className="border rounded-lg p-3 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium flex items-center gap-1">{icon}{title}</span>
        <Button data-testid={`non-invoice-cleanup-${operation}-run`} onClick={onRun} disabled={runDisabled} size="sm">
          {(starting || polling) ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Eye className="h-3 w-3 mr-1" />}
          {running
            ? `Chain running, run ${fmtInt(chain.runs_count)}, ${fmtInt(processed)}/${fmtInt(cohortCount)}`
            : runLabel}
        </Button>
        {running && (
          <Button data-testid={`non-invoice-cleanup-${operation}-stop`} onClick={onStop} disabled={stopping} size="sm" variant="outline">
            {stopping ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Stop Chain
          </Button>
        )}
        {polling && (
          <span className="text-xs text-muted-foreground tabular-nums flex items-center gap-1">
            <RefreshCw className="h-3 w-3 animate-spin" />
            {elapsed}s
          </span>
        )}
      </div>

      {chain && (
        <div className="text-xs space-y-2" data-testid={`non-invoice-cleanup-${operation}-chain-summary`}>
          <div className="flex items-center gap-1">
            <Badge variant={running ? 'default' : chain.status === 'error' ? 'destructive' : 'outline'}>
              {chain.status}
            </Badge>
            <span className="text-muted-foreground">
              run {fmtInt(chain.runs_count)}
              {cohortCount != null && <> -- {fmtInt(processed)} of {fmtInt(cohortCount)} processed</>}
            </span>
            {stalled && (
              <span className="text-amber-600 dark:text-amber-400">
                last active {heartbeatAge}s ago -- watchdog will resume it
              </span>
            )}
          </div>

          <div className="text-muted-foreground" data-testid={`non-invoice-cleanup-${operation}-last-run`}>
            Last run: {fmtTimestamp(chain.heartbeat_at)}
          </div>

          {live && (
            <div className="flex flex-wrap gap-3">
              <span>{operation === 'apply' ? 'Cleaned' : 'Would clean'}: <strong>{fmtInt(operation === 'apply' ? live.excluded : live.would_exclude)}</strong></span>
              <span>{operation === 'apply' ? 'Flagged suspect' : 'Would flag suspect'}: <strong>{fmtInt(operation === 'apply' ? live.suspected : live.would_suspect)}</strong></span>
              <span>Scanned: <strong>{fmtInt(processed)}</strong></span>
              {operation === 'preview' && (
                <span>Newly in scope (resolved entity / rejected): <strong>{fmtInt(live.would_exclude_newly_in_scope)}</strong></span>
              )}
              {operation === 'apply' && (
                <span className={live.pending_confirmation > 0 ? 'text-amber-600 dark:text-amber-400' : undefined}>
                  Pending confirmation: <strong>{fmtInt(live.pending_confirmation)}</strong>
                </span>
              )}
            </div>
          )}

          {Object.keys(byReason).length > 0 && (
            <div className="flex flex-wrap gap-1" data-testid={`non-invoice-cleanup-${operation}-tallies`}>
              {Object.entries(byReason).map(([reason, count]) => (
                <Badge key={reason} variant="outline" className="text-[10px]">
                  {reason}: {count}
                </Badge>
              ))}
            </div>
          )}

          {/* Population (b): quarantined docs the classifier does NOT
              consider junk-shaped -- a real receipt/invoice whose amount
              failed to parse (e.g. "Your receipt from Anthropic" at
              EUR0.00). Flagged for attention, never excluded -- surfaced
              separately from the exclusion samples above so it can never be
              read as "this was cleaned". */}
          {(live?.suspect_samples?.length > 0) && (
            <div className="border rounded overflow-x-auto" data-testid={`non-invoice-cleanup-${operation}-suspect-samples`}>
              <div className="px-2 py-1 font-medium bg-muted/50">amount_parse_suspect (not excluded -- needs a human to fix the amount)</div>
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-2 py-1 font-medium">Subject</th>
                    <th className="text-left px-2 py-1 font-medium">Sender</th>
                  </tr>
                </thead>
                <tbody>
                  {live.suspect_samples.map((r, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="px-2 py-1">{r.subject || '-'}</td>
                      <td className="px-2 py-1">{r.sender || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Explain-yourself readout: the positive evidence that fired for
              each sample, not just the reason code -- an operator can
              verify the classification directly instead of trusting a
              label (CLAUDE.md section 7 item 9). Vendor/Entity/Description/
              Date columns and the Scope badge let an operator review the
              full would-exclude list -- including the newly-in-scope
              subset (a resolved entity, or review_status=='rejected') --
              before confirming the broadened-cohort gate below. */}
          {Object.entries(samples).map(([reason, rows]) => (
            rows?.length > 0 && (
              <div key={reason} className="border rounded overflow-x-auto" data-testid={`non-invoice-cleanup-${operation}-samples-${reason}`}>
                <div className="px-2 py-1 font-medium bg-muted/50">{reason}</div>
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-2 py-1 font-medium">Subject</th>
                      <th className="text-left px-2 py-1 font-medium">Sender</th>
                      <th className="text-left px-2 py-1 font-medium">Vendor</th>
                      <th className="text-left px-2 py-1 font-medium">Entity</th>
                      <th className="text-left px-2 py-1 font-medium">Date</th>
                      <th className="text-left px-2 py-1 font-medium">Evidence</th>
                      <th className="text-left px-2 py-1 font-medium">Scope</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="px-2 py-1">{r.subject || '-'}</td>
                        <td className="px-2 py-1">{r.sender || '-'}</td>
                        <td className="px-2 py-1">{r.vendor || '-'}</td>
                        <td className="px-2 py-1">{r.entity_name || r.entity_id || '-'}</td>
                        <td className="px-2 py-1">{r.date || '-'}</td>
                        <td className="px-2 py-1 text-muted-foreground">{r.evidence || '-'}</td>
                        <td className="px-2 py-1">
                          {r.newly_in_scope ? (
                            <Badge className="text-[10px] bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
                              newly in scope
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-[10px]">original</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ))}

          {/* Apply-only: docs that ARE junk-shaped and newly in scope but
              were NOT written because the broadened-cohort gate is not yet
              confirmed. Distinct from the excluded tally above -- these
              rows are still plain-listed on Supplier Invoices right now. */}
          {operation === 'apply' && (live?.pending_confirmation_samples) && Object.entries(live.pending_confirmation_samples).map(([reason, rows]) => (
            rows?.length > 0 && (
              <div key={`pending-${reason}`} className="border rounded overflow-x-auto" data-testid={`non-invoice-cleanup-apply-pending-confirmation-${reason}`}>
                <div className="px-2 py-1 font-medium bg-amber-50 dark:bg-amber-950/30">
                  {reason} -- held back pending broadened-cohort gate confirmation
                </div>
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-2 py-1 font-medium">Subject</th>
                      <th className="text-left px-2 py-1 font-medium">Vendor</th>
                      <th className="text-left px-2 py-1 font-medium">Entity</th>
                      <th className="text-left px-2 py-1 font-medium">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="px-2 py-1">{r.subject || '-'}</td>
                        <td className="px-2 py-1">{r.vendor || '-'}</td>
                        <td className="px-2 py-1">{r.entity_name || r.entity_id || '-'}</td>
                        <td className="px-2 py-1">{r.date || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ))}

          {/* The real terminal error and status from the chain/job doc --
              never a bare "Failed to run preview" string. */}
          {errorText && <p className="text-red-600 dark:text-red-400">{errorText}</p>}
        </div>
      )}
    </div>
  );
}

export function NonInvoiceCleanupPanel() {
  const [previewChain, setPreviewChain] = useState(null);
  const [previewProgress, setPreviewProgress] = useState(null);
  const [previewFailed, setPreviewFailed] = useState(null);
  const [previewStarting, setPreviewStarting] = useState(false);
  const [previewStopping, setPreviewStopping] = useState(false);

  const [applyChain, setApplyChain] = useState(null);
  const [applyProgress, setApplyProgress] = useState(null);
  const [applyFailed, setApplyFailed] = useState(null);
  const [applyStarting, setApplyStarting] = useState(false);
  const [applyStopping, setApplyStopping] = useState(false);

  const fetchPreviewChain = useCallback(async () => {
    try {
      const { data } = await diagnosticsAPI.nonInvoiceCleanupPreviewChainStatus();
      setPreviewChain(data?.chain || null);
    } catch {
      // Non-fatal: the chain summary just won't render/update this tick.
    }
  }, []);

  const fetchApplyChain = useCallback(async () => {
    try {
      const { data } = await diagnosticsAPI.nonInvoiceCleanupApplyChainStatus();
      setApplyChain(data?.chain || null);
    } catch {
      // Non-fatal.
    }
  }, []);

  const previewPoll = useJobPolling({
    onCompleted: useCallback((result) => {
      setPreviewProgress(null);
      setPreviewFailed(null);
      fetchPreviewChain();
      toast.success(result?.truncated ? 'Preview run complete -- continuing chain' : 'Preview complete');
    }, [fetchPreviewChain]),
    onFailed: useCallback((error, lastProgress) => {
      setPreviewProgress((prev) => lastProgress ?? prev);
      setPreviewFailed(error);
      fetchPreviewChain();
      toast.error(error ?? 'Preview run failed');
    }, [fetchPreviewChain]),
    onProgress: useCallback((progress) => setPreviewProgress(progress), []),
  });

  const applyPoll = useJobPolling({
    onCompleted: useCallback((result) => {
      setApplyProgress(null);
      setApplyFailed(null);
      fetchApplyChain();
      toast.success(result?.truncated ? 'Apply run complete -- continuing chain' : 'Apply complete');
    }, [fetchApplyChain]),
    onFailed: useCallback((error, lastProgress) => {
      setApplyProgress((prev) => lastProgress ?? prev);
      setApplyFailed(error);
      fetchApplyChain();
      toast.error(error ?? 'Apply run failed');
    }, [fetchApplyChain]),
    onProgress: useCallback((progress) => setApplyProgress(progress), []),
  });

  // Recover in-flight/last-finished job on mount for each operation
  // independently, mirroring InvoiceTaxonomyBackfillPanel.jsx.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.nonInvoiceCleanupJobLatest('preview');
        const job = data?.job;
        if (job?.status === 'running') {
          setPreviewProgress(job.progress || null);
          previewPoll.start(job._id);
        } else if (job?.status === 'failed') {
          setPreviewProgress(job.progress || null);
          setPreviewFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.nonInvoiceCleanupJobLatest('apply');
        const job = data?.job;
        if (job?.status === 'running') {
          setApplyProgress(job.progress || null);
          applyPoll.start(job._id);
        } else if (job?.status === 'failed') {
          setApplyProgress(job.progress || null);
          setApplyFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchPreviewChain(); }, [fetchPreviewChain]);
  useEffect(() => { fetchApplyChain(); }, [fetchApplyChain]);

  // Keep polling chain status while running, including the gap between one
  // run finishing and its successor starting.
  useEffect(() => {
    if (previewChain?.status !== 'running') return undefined;
    const id = setInterval(fetchPreviewChain, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [previewChain?.status, fetchPreviewChain]);

  useEffect(() => {
    if (applyChain?.status !== 'running') return undefined;
    const id = setInterval(fetchApplyChain, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [applyChain?.status, fetchApplyChain]);

  const runPreview = async () => {
    setPreviewStarting(true);
    setPreviewFailed(null);
    try {
      const { data } = await diagnosticsAPI.nonInvoiceCleanupPreviewChainStart();
      if (data.job_id) {
        previewPoll.start(data.job_id);
      } else if (data.status === 'chain_running') {
        toast.error('A preview chain is already running -- it will continue on its own.');
      } else if (data.status === 'chain_unavailable') {
        toast.error('Chain became unavailable while starting -- try again.');
      }
      fetchPreviewChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewStarting(false);
    }
  };

  const runApply = async () => {
    const cohortCount = previewChain?.metadata?.cohort_count ?? applyChain?.metadata?.cohort_count;
    if (!window.confirm(
      `Flag up to ${cohortCount ?? 'the'} doc(s) as excluded_as_non_invoice? ` +
      'This is a soft flag only -- no deletion, Drive files untouched.'
    )) return;
    setApplyStarting(true);
    setApplyFailed(null);
    try {
      const { data } = await diagnosticsAPI.nonInvoiceCleanupApplyChainStart();
      if (data.job_id) {
        applyPoll.start(data.job_id);
      } else if (data.status === 'chain_running') {
        toast.error('An apply chain is already running -- it will continue on its own.');
      } else if (data.status === 'chain_unavailable') {
        toast.error('Chain became unavailable while starting -- try again.');
      }
      fetchApplyChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplyStarting(false);
    }
  };

  const stopPreviewChain = async () => {
    if (!previewChain?._id) return;
    if (!window.confirm('Stop the running Preview chain? The current run finishes its time window normally.')) return;
    setPreviewStopping(true);
    try {
      await diagnosticsAPI.nonInvoiceCleanupPreviewChainStop(previewChain._id);
      toast.success('Stop requested -- the chain will halt after its current run finishes.');
      fetchPreviewChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewStopping(false);
    }
  };

  const stopApplyChain = async () => {
    if (!applyChain?._id) return;
    if (!window.confirm('Stop the running Apply chain? The current run finishes its time window normally.')) return;
    setApplyStopping(true);
    try {
      await diagnosticsAPI.nonInvoiceCleanupApplyChainStop(applyChain._id);
      toast.success('Stop requested -- the chain will halt after its current run finishes.');
      fetchApplyChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplyStopping(false);
    }
  };

  // Read-only PDF-bypass near-miss check: confirms/refutes whether the
  // deterministic gate's has_pdf exemption is why PDF-carrying junk (a CV,
  // a marketing brochure) survives the cleanup cohort uncaught -- see
  // INVOICE_CLEANER_PHASE_A_FINDINGS.md section 2. No chain/polling needed,
  // it's a single bounded GET.
  const [nearMiss, setNearMiss] = useState(null);
  const [nearMissLoading, setNearMissLoading] = useState(false);

  const checkPdfBypassNearMiss = async () => {
    setNearMissLoading(true);
    try {
      const { data } = await diagnosticsAPI.nonInvoiceCleanupPdfBypassNearMiss();
      setNearMiss(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setNearMissLoading(false);
    }
  };

  // Broadened-cohort gate (Commit 2): entity resolution doesn't rescue a
  // junk-shaped doc, so the newly-in-scope subset (a resolved entity,
  // and/or review_status=='rejected') is eligible for exclusion too -- but
  // Apply won't write it until this is explicitly confirmed here, after
  // reviewing the would-exclude list (vendor/entity/description/date)
  // above. Defaults to unconfirmed.
  const [gate, setGate] = useState(null);
  const [gateConfirming, setGateConfirming] = useState(false);

  const fetchGate = useCallback(async () => {
    try {
      const { data } = await diagnosticsAPI.nonInvoiceCleanupBroadenedCohortGateStatus();
      setGate(data);
    } catch {
      // Non-fatal: the gate section just won't render/update this tick.
    }
  }, []);

  useEffect(() => { fetchGate(); }, [fetchGate]);

  const confirmGate = async () => {
    if (!window.confirm(
      'Confirm that you have reviewed the Preview would-exclude list above, including the ' +
      '"newly in scope" rows (a resolved entity, or review_status==\'rejected\')? ' +
      'Apply will start writing excluded_as_non_invoice for that subset immediately after this.'
    )) return;
    setGateConfirming(true);
    try {
      const { data } = await diagnosticsAPI.nonInvoiceCleanupBroadenedCohortGateConfirm();
      setGate(data);
      toast.success('Broadened-cohort gate confirmed -- Apply will now exclude the newly-in-scope subset.');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setGateConfirming(false);
    }
  };

  // Type-classify suspects (Fan's document-type classification, Commit 2):
  // retroactive AI classification pass over the standing suspect
  // population. Own Preview/Apply pair -- distinct chains, distinct AI
  // call per doc, so kept structurally separate from the ChainSection
  // above (whose tally shape -- by_reason/samples-by-reason -- doesn't
  // match this pass's would_reject_not_financial/stays_suspect_financial/
  // needs_type_review buckets).
  const [classifyPreviewChain, setClassifyPreviewChain] = useState(null);
  const [classifyPreviewProgress, setClassifyPreviewProgress] = useState(null);
  const [classifyPreviewFailed, setClassifyPreviewFailed] = useState(null);
  const [classifyPreviewStarting, setClassifyPreviewStarting] = useState(false);

  const [classifyApplyChain, setClassifyApplyChain] = useState(null);
  const [classifyApplyProgress, setClassifyApplyProgress] = useState(null);
  const [classifyApplyFailed, setClassifyApplyFailed] = useState(null);
  const [classifyApplyStarting, setClassifyApplyStarting] = useState(false);

  const fetchClassifyPreviewChain = useCallback(async () => {
    try {
      const { data } = await diagnosticsAPI.documentTypeClassificationPreviewChainStatus();
      setClassifyPreviewChain(data?.chain || null);
    } catch {
      // Non-fatal.
    }
  }, []);

  const fetchClassifyApplyChain = useCallback(async () => {
    try {
      const { data } = await diagnosticsAPI.documentTypeClassificationApplyChainStatus();
      setClassifyApplyChain(data?.chain || null);
    } catch {
      // Non-fatal.
    }
  }, []);

  const classifyPreviewPoll = useJobPolling({
    pollFn: diagnosticsAPI.documentTypeClassificationJobStatus,
    onCompleted: useCallback((result) => {
      setClassifyPreviewProgress(null);
      setClassifyPreviewFailed(null);
      fetchClassifyPreviewChain();
      toast.success(result?.truncated ? 'Preview run complete -- continuing chain' : 'Preview complete');
    }, [fetchClassifyPreviewChain]),
    onFailed: useCallback((error, lastProgress) => {
      setClassifyPreviewProgress((prev) => lastProgress ?? prev);
      setClassifyPreviewFailed(error);
      fetchClassifyPreviewChain();
      toast.error(error ?? 'Preview run failed');
    }, [fetchClassifyPreviewChain]),
    onProgress: useCallback((progress) => setClassifyPreviewProgress(progress), []),
  });

  const classifyApplyPoll = useJobPolling({
    pollFn: diagnosticsAPI.documentTypeClassificationJobStatus,
    onCompleted: useCallback((result) => {
      setClassifyApplyProgress(null);
      setClassifyApplyFailed(null);
      fetchClassifyApplyChain();
      toast.success(result?.truncated ? 'Apply run complete -- continuing chain' : 'Apply complete');
    }, [fetchClassifyApplyChain]),
    onFailed: useCallback((error, lastProgress) => {
      setClassifyApplyProgress((prev) => lastProgress ?? prev);
      setClassifyApplyFailed(error);
      fetchClassifyApplyChain();
      toast.error(error ?? 'Apply run failed');
    }, [fetchClassifyApplyChain]),
    onProgress: useCallback((progress) => setClassifyApplyProgress(progress), []),
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.documentTypeClassificationJobLatest('preview');
        const job = data?.job;
        if (job?.status === 'running') {
          setClassifyPreviewProgress(job.progress || null);
          classifyPreviewPoll.start(job._id);
        } else if (job?.status === 'failed') {
          setClassifyPreviewProgress(job.progress || null);
          setClassifyPreviewFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.documentTypeClassificationJobLatest('apply');
        const job = data?.job;
        if (job?.status === 'running') {
          setClassifyApplyProgress(job.progress || null);
          classifyApplyPoll.start(job._id);
        } else if (job?.status === 'failed') {
          setClassifyApplyProgress(job.progress || null);
          setClassifyApplyFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchClassifyPreviewChain(); }, [fetchClassifyPreviewChain]);
  useEffect(() => { fetchClassifyApplyChain(); }, [fetchClassifyApplyChain]);

  useEffect(() => {
    if (classifyPreviewChain?.status !== 'running') return undefined;
    const id = setInterval(fetchClassifyPreviewChain, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [classifyPreviewChain?.status, fetchClassifyPreviewChain]);

  useEffect(() => {
    if (classifyApplyChain?.status !== 'running') return undefined;
    const id = setInterval(fetchClassifyApplyChain, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [classifyApplyChain?.status, fetchClassifyApplyChain]);

  const runClassifyPreview = async () => {
    setClassifyPreviewStarting(true);
    setClassifyPreviewFailed(null);
    try {
      const { data } = await diagnosticsAPI.documentTypeClassificationPreviewChainStart();
      if (data.job_id) {
        classifyPreviewPoll.start(data.job_id);
      } else if (data.status === 'chain_running') {
        toast.error('A preview chain is already running -- it will continue on its own.');
      }
      fetchClassifyPreviewChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setClassifyPreviewStarting(false);
    }
  };

  const runClassifyApply = async () => {
    const cohortCount = classifyPreviewChain?.metadata?.cohort_count;
    if (!window.confirm(
      `Classify up to ${cohortCount ?? 'the'} suspect doc(s) and apply the disposition (reject not-financial, ` +
      'flag needs-type-review, or leave financial docs untouched)? Requires a completed Preview over the same cohort.'
    )) return;
    setClassifyApplyStarting(true);
    setClassifyApplyFailed(null);
    try {
      const { data } = await diagnosticsAPI.documentTypeClassificationApplyChainStart();
      if (data.job_id) {
        classifyApplyPoll.start(data.job_id);
      } else if (data.status === 'chain_running') {
        toast.error('An apply chain is already running -- it will continue on its own.');
      }
      fetchClassifyApplyChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setClassifyApplyStarting(false);
    }
  };

  const classifyPreviewLive = classifyPreviewProgress || classifyPreviewChain?.seed;
  const classifyApplyLive = classifyApplyProgress || classifyApplyChain?.seed;
  const classifyPreviewComplete = classifyPreviewChain?.status === 'complete';

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Trash2 className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Non-Invoice Cleanup</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Flags existing Gmail-sourced junk (bounces, calendar invites, no-PDF/no-amount
          mail) that was ingested before the junk gate existed. Cohort covers both
          quarantined and pending_review docs unresolved to a real entity -- soft flag
          only, no deletion, Drive files untouched. Both Preview and Apply are
          budget-boxed chains: each run time-boxes itself and schedules its own
          successor from the persisted cursor/tallies until the cohort converges.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <ChainSection
          operation="preview"
          title="Preview"
          icon={<Eye className="h-3 w-3" />}
          chain={previewChain}
          progress={previewProgress}
          failed={previewFailed}
          polling={previewPoll.polling}
          elapsed={previewPoll.elapsed}
          starting={previewStarting}
          stopping={previewStopping}
          onRun={runPreview}
          onStop={stopPreviewChain}
          runLabel="Preview"
          runDisabled={previewStarting || previewPoll.polling || previewChain?.status === 'running'}
        />
        <ChainSection
          operation="apply"
          title="Apply"
          icon={<Play className="h-3 w-3" />}
          chain={applyChain}
          progress={applyProgress}
          failed={applyFailed}
          polling={applyPoll.polling}
          elapsed={applyPoll.elapsed}
          starting={applyStarting}
          stopping={applyStopping}
          onRun={runApply}
          onStop={stopApplyChain}
          runLabel={previewChain?.metadata?.cohort_count != null ? `Apply (${fmtInt(previewChain.metadata.cohort_count)} candidates)` : 'Apply'}
          runDisabled={applyStarting || applyPoll.polling || applyChain?.status === 'running'}
        />

        <div className="border rounded-lg p-3 space-y-2" data-testid="non-invoice-cleanup-broadened-cohort-gate">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium">Broadened-Cohort Gate</span>
            <Badge
              data-testid="non-invoice-cleanup-broadened-cohort-gate-status"
              className={gate?.confirmed
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'}
            >
              {gate?.confirmed ? 'Confirmed' : 'Not confirmed'}
            </Badge>
            {!gate?.confirmed && (
              <Button
                data-testid="non-invoice-cleanup-broadened-cohort-gate-confirm"
                onClick={confirmGate}
                disabled={gateConfirming}
                size="sm"
                variant="outline"
              >
                {gateConfirming ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                Confirm
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Entity resolution does not rescue a junk-shaped doc -- a resolved entity, and/or
            review_status=='rejected', is now eligible for exclusion (Commit 2) just like the
            original unassigned-entity cohort. Until confirmed here, Apply counts these as
            "Pending confirmation" and leaves them untouched (still visible on the Supplier
            Invoices list) instead of writing excluded_as_non_invoice. Review the "newly in
            scope" rows in the Preview would-exclude tables above (vendor/entity/description/
            date) before confirming -- this only needs to happen once.
          </p>
          {gate?.confirmed && (
            <p className="text-xs text-muted-foreground" data-testid="non-invoice-cleanup-broadened-cohort-gate-meta">
              Confirmed by {gate.confirmed_by || 'unknown'} at {fmtTimestamp(gate.confirmed_at)}
            </p>
          )}
        </div>

        <div className="border rounded-lg p-3 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium flex items-center gap-1">
              <Search className="h-3 w-3" />PDF-Bypass Near-Miss
            </span>
            <Button
              data-testid="non-invoice-cleanup-pdf-bypass-check"
              onClick={checkPdfBypassNearMiss}
              disabled={nearMissLoading}
              size="sm"
              variant="outline"
            >
              {nearMissLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Check
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Read-only. Any doc with a PDF attachment skips the no-amount junk
            check entirely, regardless of what the PDF contains -- protects real
            invoice PDFs, but also lets a CV or a property brochure through
            uncaught. Reports how many cohort docs would have been classified as
            junk if that exemption didn't apply. No writes, no classifier change.
          </p>
          {nearMiss && (
            <div className="text-xs space-y-2" data-testid="non-invoice-cleanup-pdf-bypass-result">
              <div className="flex flex-wrap gap-3">
                <span>Scanned: <strong>{fmtInt(nearMiss.scanned)}</strong></span>
                <span>Near-miss (PDF exemption is the deciding factor): <strong>{fmtInt(nearMiss.near_miss_count)}</strong></span>
              </div>
              {nearMiss.samples?.length > 0 && (
                <div className="border rounded overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-2 py-1 font-medium">Subject</th>
                        <th className="text-left px-2 py-1 font-medium">Sender</th>
                        <th className="text-left px-2 py-1 font-medium">Attachment(s)</th>
                        <th className="text-left px-2 py-1 font-medium">Would-be reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {nearMiss.samples.map((r, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="px-2 py-1">{r.subject || '-'}</td>
                          <td className="px-2 py-1">{r.sender || '-'}</td>
                          <td className="px-2 py-1 text-muted-foreground">{(r.attachment_filenames || []).join(', ') || '-'}</td>
                          <td className="px-2 py-1 text-muted-foreground">{r.would_be_reason_without_pdf_exemption || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="border rounded-lg p-3 space-y-2" data-testid="document-type-classification-section">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium flex items-center gap-1">
              <Search className="h-3 w-3" />Type-classify suspects
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            Runs Commit 1's document-type classifier (services/document_classification.py) over the
            standing amount_parse_suspect population plus pending_review docs still missing an amount
            -- the "is this really a CV/marketing/correspondence, not an invoice" question the amount
            gate alone can't answer. A confidently not-financial doc is rejected; a financial doc is
            left untouched; anything below the confidence threshold only flags needs_type_review.
            Reads a stored Drive file if there is one, otherwise tries the original Gmail message's
            attachment, otherwise subject + body text alone -- the doc's own text stored at ingestion
            if the live message can't be reached (Source column below). Only a doc with no Drive file,
            no live Gmail content, and no text ever stored gets no verdict (would_flag_source_gone /
            source_gone, flagged needs_type_review for a human).
          </p>

          <div className="flex items-center gap-2 flex-wrap">
            <Button
              data-testid="document-type-classification-preview-run"
              onClick={runClassifyPreview}
              disabled={classifyPreviewStarting || classifyPreviewPoll.polling || classifyPreviewChain?.status === 'running'}
              size="sm"
            >
              {(classifyPreviewStarting || classifyPreviewPoll.polling) ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Eye className="h-3 w-3 mr-1" />}
              {classifyPreviewChain?.status === 'running'
                ? `Chain running, run ${fmtInt(classifyPreviewChain.runs_count)}, ${fmtInt(classifyPreviewLive?.cohort_processed)}/${fmtInt(classifyPreviewChain?.metadata?.cohort_count)}`
                : 'Preview'}
            </Button>
            <Button
              data-testid="document-type-classification-apply-run"
              onClick={runClassifyApply}
              disabled={classifyApplyStarting || classifyApplyPoll.polling || classifyApplyChain?.status === 'running' || !classifyPreviewComplete}
              size="sm"
              variant="outline"
            >
              {(classifyApplyStarting || classifyApplyPoll.polling) ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Play className="h-3 w-3 mr-1" />}
              {classifyApplyChain?.status === 'running'
                ? `Chain running, run ${fmtInt(classifyApplyChain.runs_count)}, ${fmtInt(classifyApplyLive?.cohort_processed)}/${fmtInt(classifyApplyChain?.metadata?.cohort_count)}`
                : 'Apply'}
            </Button>
            {!classifyPreviewComplete && (
              <span className="text-xs text-muted-foreground">Apply needs a completed Preview over the current cohort first.</span>
            )}
          </div>

          {classifyPreviewFailed && <p className="text-xs text-red-600 dark:text-red-400">{classifyPreviewFailed}</p>}
          {classifyApplyFailed && <p className="text-xs text-red-600 dark:text-red-400">{classifyApplyFailed}</p>}

          {[
            { label: 'Preview', live: classifyPreviewLive, chain: classifyPreviewChain, op: 'preview' },
            { label: 'Apply', live: classifyApplyLive, chain: classifyApplyChain, op: 'apply' },
          ].map(({ label, live, chain, op }) => (
            live && (
              <div key={op} className="text-xs space-y-2" data-testid={`document-type-classification-${op}-result`}>
                <div className="text-muted-foreground">{label} -- last active {fmtTimestamp(chain?.heartbeat_at)}</div>
                {live.tallies && Object.keys(live.tallies).length > 0 && (
                  <div className="flex flex-wrap gap-1" data-testid={`document-type-classification-${op}-tallies`}>
                    {Object.entries(live.tallies).map(([key, count]) => (
                      <Badge key={key} variant="outline" className="text-[10px]">
                        {key}: {count}
                      </Badge>
                    ))}
                  </div>
                )}
                {live.samples && Object.entries(live.samples).map(([bucket, rows]) => (
                  rows?.length > 0 && (
                    <div key={bucket} className="border rounded overflow-x-auto" data-testid={`document-type-classification-${op}-samples-${bucket}`}>
                      <div className="px-2 py-1 font-medium bg-muted/50">{bucket}</div>
                      <table className="w-full">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="text-left px-2 py-1 font-medium">Doc</th>
                            <th className="text-left px-2 py-1 font-medium">Subject</th>
                            <th className="text-left px-2 py-1 font-medium">Vendor</th>
                            <th className="text-left px-2 py-1 font-medium">Proposed type</th>
                            <th className="text-left px-2 py-1 font-medium">Confidence</th>
                            <th className="text-left px-2 py-1 font-medium">Proposed disposition</th>
                            <th className="text-left px-2 py-1 font-medium">Source</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((r, i) => (
                            <tr key={i} className="border-b last:border-0">
                              <td className="px-2 py-1 font-mono">{r.id || '-'}</td>
                              <td className="px-2 py-1">{r.subject || '-'}</td>
                              <td className="px-2 py-1">{r.vendor || '-'}</td>
                              <td className="px-2 py-1">{r.proposed_type || '-'}</td>
                              <td className="px-2 py-1">{r.confidence != null ? `${Math.round(r.confidence * 100)}%` : '-'}</td>
                              <td className="px-2 py-1">{r.proposed_disposition || '-'}</td>
                              <td className="px-2 py-1 text-muted-foreground">{r.classified_from || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
                ))}
              </div>
            )
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
